import { getLimitFromString, LimitKeys } from 'api/clients/TierUsageClient'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useEffect, useMemo } from 'react'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export interface Props {
  limitKey: LimitKeys | 'integrations' | 'published_posts'
  integrationType?: ExternalIntegrationEnum
  overrideTier?: PaymentPlanKey
}

export const useTierUsageLimits = ({ limitKey, integrationType, overrideTier }: Props) => {
  const tierUsageStore = useRootStore().tierUsageStore
  const workspace = useCurrentWorkspace()

  const {
    tierLimits: currentTierLimits,
    tierInfo,
    currentSubscription,
    paymentPlans,
    initialized,
    loading,
  } = tierUsageStore

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  const nextTierKey = useMemo(
    () => (currentSubscription ? getNextTierKey(currentSubscription?.plan, limitKey) : undefined),
    [currentSubscription, limitKey],
  )

  const nextTierPlan = useMemo(
    () => paymentPlans?.find((plan) => plan.key === nextTierKey),
    [nextTierKey, paymentPlans],
  )

  const currentTierPlan = useMemo(() => {
    const planKey = overrideTier || currentSubscription?.plan
    return paymentPlans?.find((plan) => plan.key === planKey)
  }, [paymentPlans, currentSubscription, overrideTier])

  let limit = Infinity
  let usage = 0

  const tierLimits = overrideTier ? currentTierPlan?.base_price_meta : currentTierLimits

  switch (limitKey) {
    case 'integrations':
    case 'published_posts':
      limit = getLimitFromString(tierLimits?.integrations_limit)
      usage = integrationType ? tierInfo?.[limitKey]?.[integrationType] || 0 : 0
      break
    default:
      limit = getLimitFromString(tierLimits?.[`${limitKey}_limit`])
      usage = tierInfo?.[limitKey] || 0
  }

  const limitOverage = usage > limit ? usage - limit : 0

  return {
    limit,
    usage,
    atLimit: usage >= limit,
    limitOverage,
    nextTierPlan,
    currentTierPlan,
  }
}

export const getNextTierKey = (currentPlan: PaymentPlanKey, limitKey?: Props['limitKey']) => {
  switch (currentPlan) {
    case PaymentPlanKey.BlazeCreator:
      if (limitKey === 'seats') {
        return PaymentPlanKey.BlazeTeam
      }
      return PaymentPlanKey.BlazePro
    case PaymentPlanKey.BlazePro:
      return PaymentPlanKey.BlazeTeam
    case PaymentPlanKey.BlazeTeam:
      return PaymentPlanKey.BlazeAgency
    case PaymentPlanKey.BlazeAgency:
      return PaymentPlanKey.BlazeEnterprise
    default:
      return PaymentPlanKey.BlazePro
  }
}
